// src/pages/PublicJobsPage.js — the public NJJBA Job Board
// [marker: finish-up v1]
// Anyone can read it. Shown at /jobs (stand-alone page to link from the website) and
// /embed/jobs (for embedding in a Wix page). Members post from the member portal.
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api/jobs-public' });
export const JOB_TYPE_LABELS = { full_time: 'Full-time', part_time: 'Part-time', contract: 'Contract', temporary: 'Temporary', internship: 'Internship', volunteer: 'Volunteer' };
export const WORKPLACE_LABELS = { on_site: 'On-site', hybrid: 'Hybrid', remote: 'Remote' };
const NAVY = '#1a3a5c'; const GOLD = '#c8a84b';
const fmtDay = (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '');
const ago = (s) => {
  if (!s) return '';
  const days = Math.floor((Date.now() - new Date(`${s}T12:00:00`).getTime()) / 86400000);
  if (days <= 0) return 'Posted today';
  if (days === 1) return 'Posted yesterday';
  if (days < 30) return `Posted ${days} days ago`;
  return `Posted ${fmtDay(s)}`;
};

export default function PublicJobsPage({ embedded }) {
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [where, setWhere] = useState('');
  const [open, setOpen] = useState(() => {
    const id = new URLSearchParams(window.location.search).get('job');
    return id ? parseInt(id, 10) : null;
  });

  useEffect(() => {
    api.get('/').then(r => setJobs(r.data || [])).catch(() => setError('Unable to load the job board. Please try again later.'));
  }, []);
  useEffect(() => {
    if (open && jobs) { const el = document.getElementById(`job-${open}`); if (el) el.scrollIntoView({ block: 'start' }); }
  }, [open, jobs]);

  const shown = useMemo(() => (jobs || []).filter(j => {
    if (type && j.job_type !== type) return false;
    if (where && j.workplace !== where) return false;
    if (q.trim()) {
      const hay = `${j.title} ${j.company_name || ''} ${j.location || ''} ${j.description}`.toLowerCase();
      return q.toLowerCase().split(/\s+/).every(w => hay.includes(w));
    }
    return true;
  }), [jobs, q, type, where]);

  const postLink = '/portal/jobs?new=1';

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif', background: embedded ? 'transparent' : '#f6f7f9', minHeight: embedded ? 0 : '100vh', color: '#1f2937' }}>
      {!embedded && (
        <div style={{ background: NAVY, color: 'white', padding: '28px 16px 26px', borderBottom: `4px solid ${GOLD}` }}>
          <div style={{ maxWidth: 920, margin: '0 auto' }}>
            <div style={{ fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: GOLD, fontWeight: 700 }}>New Jersey Jewish Business Alliance</div>
            <h1 style={{ margin: '6px 0 4px', fontSize: '2rem', fontFamily: 'Georgia, serif' }}>Job Board</h1>
            <div style={{ opacity: 0.85 }}>Openings at NJJBA member businesses.</div>
          </div>
        </div>
      )}
      <div style={{ maxWidth: 920, margin: '0 auto', padding: embedded ? '8px 12px 24px' : '22px 16px 48px' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search jobs, companies, places…" aria-label="Search jobs"
            style={{ ...inp, flex: '1 1 240px' }} />
          <select value={type} onChange={e => setType(e.target.value)} style={inp} aria-label="Job type">
            <option value="">All job types</option>
            {Object.entries(JOB_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={where} onChange={e => setWhere(e.target.value)} style={inp} aria-label="Workplace">
            <option value="">On-site, hybrid or remote</option>
            {Object.entries(WORKPLACE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <a href={postLink} target={embedded ? '_blank' : undefined} rel="noreferrer"
             style={{ background: GOLD, color: NAVY, fontWeight: 700, padding: '10px 16px', borderRadius: 6, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            + Post a job <span style={{ fontWeight: 400, fontSize: '0.8rem' }}>(members)</span>
          </a>
        </div>

        {error && <div style={{ padding: 20, color: '#b91c1c' }}>{error}</div>}
        {!jobs && !error && <div style={{ padding: 30, textAlign: 'center', color: '#6b7280' }}>Loading jobs…</div>}
        {jobs && (
          <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 8 }}>
            {shown.length === jobs.length ? `${jobs.length} open position${jobs.length === 1 ? '' : 's'}` : `${shown.length} of ${jobs.length} positions`}
          </div>
        )}
        {jobs && shown.length === 0 && (
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 30, textAlign: 'center', color: '#6b7280' }}>
            {jobs.length ? 'No jobs match your search.' : 'No jobs are posted right now. Check back soon!'}
          </div>
        )}
        {shown.map(j => {
          const isOpen = open === j.id;
          return (
            <article key={j.id} id={`job-${j.id}`} style={{ background: 'white', border: `1px solid ${isOpen ? NAVY : '#e5e7eb'}`, borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
              <button onClick={() => setOpen(isOpen ? null : j.id)} aria-expanded={isOpen}
                style={{ all: 'unset', boxSizing: 'border-box', display: 'block', width: '100%', padding: '14px 16px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1.08rem', color: NAVY }}>{j.title}</div>
                    <div style={{ marginTop: 2, color: '#374151' }}>{j.company_name}{j.location ? ` · ${j.location}` : ''}</div>
                  </div>
                  <span style={{ color: '#9ca3af', fontSize: '1.2rem', lineHeight: 1 }}>{isOpen ? '−' : '+'}</span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
                  {j.job_type && <Tag>{JOB_TYPE_LABELS[j.job_type]}</Tag>}
                  {j.workplace && <Tag>{WORKPLACE_LABELS[j.workplace]}</Tag>}
                  {j.salary && <Tag gold>{j.salary}</Tag>}
                  <span style={{ fontSize: '0.78rem', color: '#9ca3af', marginLeft: 4 }}>{ago(j.posted)}</span>
                </div>
              </button>
              {isOpen && (
                <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f3f4f6' }}>
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, marginTop: 12 }}>{j.description}</div>
                  <div style={{ background: '#f9fafb', borderRadius: 8, padding: '12px 14px', marginTop: 14 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>How to apply</div>
                    {j.how_to_apply && <div style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>{j.how_to_apply}</div>}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {j.apply_url && <a href={j.apply_url} target="_blank" rel="noopener noreferrer nofollow" style={btnPrimary}>Apply online ↗</a>}
                      {j.apply_email && <a href={`mailto:${j.apply_email}?subject=${encodeURIComponent(`Application: ${j.title}`)}`} style={btnSecondary}>✉ {j.apply_email}</a>}
                      {j.company_website && <a href={j.company_website} target="_blank" rel="noopener noreferrer nofollow" style={btnSecondary}>Company website ↗</a>}
                    </div>
                  </div>
                </div>
              )}
            </article>
          );
        })}
        <div style={{ textAlign: 'center', fontSize: '0.8rem', color: '#9ca3af', marginTop: 18 }}>
          NJJBA members can post openings for free from the <a href={postLink} target={embedded ? '_blank' : undefined} rel="noreferrer" style={{ color: NAVY }}>member portal</a>.
        </div>
      </div>
    </div>
  );
}
const Tag = ({ children, gold }) => (
  <span style={{ fontSize: '0.75rem', fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: gold ? '#fbf6e6' : '#eef2f7', color: gold ? '#7a5d0e' : NAVY }}>{children}</span>
);
const inp = { padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.95rem', background: 'white', minWidth: 0 };
const btnPrimary = { background: NAVY, color: 'white', padding: '9px 14px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem' };
const btnSecondary = { background: 'white', color: NAVY, border: `1px solid ${NAVY}`, padding: '8px 14px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', wordBreak: 'break-all' };
